import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { AuthApi } from '@/api';
import { tokens } from '@/api/client';
import type { User } from '@/types';

interface AuthContextValue {
  user: User | null;
  ready: boolean;
  login: (email: string, password: string, totp?: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!tokens.access) { setReady(true); return; }
    AuthApi.me()
      .then((r) => setUser(r.user))
      .catch(() => { tokens.clear(); setUser(null); })
      .finally(() => setReady(true));
  }, []);

  const login = useCallback(async (email: string, password: string, totp?: string) => {
    const r = await AuthApi.login(email, password, totp);
    tokens.set(r.accessToken, r.refreshToken);
    setUser(r.user);
  }, []);

  const logout = useCallback(async () => {
    try { await AuthApi.logout(tokens.refresh ?? undefined); } catch { /* swallow */ }
    tokens.clear();
    setUser(null);
  }, []);

  const refresh = useCallback(async () => {
    const r = await AuthApi.me();
    setUser(r.user);
  }, []);

  return (
    <AuthContext.Provider value={{ user, ready, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const v = useContext(AuthContext);
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>');
  return v;
}
